import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ChevronRight,
  FolderIcon,
  Loader2,
  PlusIcon,
  Trash,
  RefreshCw,
  Save,
  CheckCircle,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { useCategories } from '../categories/SubcategoryManager/useCategories';
import { Category } from '@/types/categories';
import * as categoryService from '@/services/categoryService';
import { QUERY_KEYS } from '@/services/productService';
import { useToast } from '@/components/ui/use-toast';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { useQueryClient } from '@tanstack/react-query';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId?: number;
  currentCategory?: Category | null;
  onCategoryChanged?: (category?: Category) => void;
  title?: string;
  description?: string;
}

export function CategoryModal({
  isOpen,
  onClose,
  productId = 0,
  currentCategory,
  onCategoryChanged,
  title,
  description,
}: CategoryModalProps) {
  const { toast } = useToast();
  const { getCategoryModalLabels } = useProductDetailPageTranslations();
  const categoryModal = getCategoryModalLabels;
  const queryClient = useQueryClient();

  // Determine if we're in product mode or general management mode
  const isProductMode = productId > 0;

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    currentCategory || null
  );
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdatingProduct, setIsUpdatingProduct] = useState(false);

  // Categories data and operations
  const {
    categories,
    isLoading,
    isError,
    refetch,
    createCategory,
    updateCategory,
    deleteCategory,
    isCreating,
    isUpdating,
    isDeleting,
  } = useCategories();

  // Reset selected category when modal opens or currentCategory changes
  useEffect(() => {
    setSelectedCategory(currentCategory || null);
  }, [isOpen, currentCategory]);

  // When a category is selected, update the edit field
  useEffect(() => {
    if (selectedCategory) {
      setEditCategoryName(selectedCategory.name);
    } else {
      setEditCategoryName('');
    }
  }, [selectedCategory]);

  // Handle modal close with state reset
  const handleClose = () => {
    setSelectedCategory(currentCategory || null);
    setNewCategoryName('');
    setEditCategoryName('');
    setSearchTerm('');
    onClose();
  };

  // Handle category selection
  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category);
  };

  // Toggle category expansion
  const toggleExpand = (categoryId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  // Handle creating a new category
  const handleCreateCategory = async () => {
    if (!newCategoryName || typeof newCategoryName !== 'string' || !newCategoryName.trim()) return;

    await createCategory(newCategoryName.trim(), selectedCategory ? selectedCategory.id : null);

    setNewCategoryName('');
  };

  // Handle updating a category
  const handleUpdateCategory = async () => {
    if (
      !selectedCategory ||
      !editCategoryName ||
      typeof editCategoryName !== 'string' ||
      !editCategoryName.trim()
    )
      return;

    await updateCategory(selectedCategory.id, {
      name: editCategoryName.trim(),
      parent: selectedCategory.parent,
    });
  };

  // Handle deleting a category
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    await deleteCategory(selectedCategory.id);
    setSelectedCategory(null);
    setShowDeleteConfirm(false);
  };

  // Handle assigning category to product
  const handleAssignCategory = async () => {
    if (!selectedCategory) {
      toast({
        title: categoryModal.error.title,
        description: categoryModal.notifications.selectCategory,
        variant: 'destructive',
      });
      return;
    }

    // If productId is 0, this is for product creation - just call the callback
    if (productId === 0) {
      // For product creation mode, we don't update anything - just notify parent
      toast({
        title: 'Success',
        description: categoryModal.notifications.categoryUpdated,
      });

      // Call the callback to notify parent component of the selected category
      if (onCategoryChanged) {
        onCategoryChanged(selectedCategory);
      }

      handleClose();
      return;
    }

    setIsUpdatingProduct(true);
    try {
      // Find the category breadcrumb path for the optimistic update
      const findCategoryBreadcrumb = (
        categories: Category[],
        id: number,
        path: string[] = []
      ): string | null => {
        for (const category of categories) {
          const currentPath = [...path, category.name];
          if (category.id === id) {
            return currentPath.join(' › ');
          }
          if (category.children) {
            const found = findCategoryBreadcrumb(category.children, id, currentPath);
            if (found) return found;
          }
        }
        return null;
      };

      const newCategoryName =
        findCategoryBreadcrumb(categories || [], selectedCategory.id) || selectedCategory.name;

      // 1. Optimistic update for seamless UI
      queryClient.setQueriesData({ queryKey: ['products'], exact: false }, (oldData: any) => {
        if (!oldData) return oldData;

        // Handle direct array response
        if (Array.isArray(oldData)) {
          return oldData.map((product: any) =>
            product.id === productId
              ? {
                  ...product,
                  category_id: selectedCategory.id,
                  category_name: newCategoryName,
                }
              : product
          );
        }

        // Handle paginated response {results: [...], count: X}
        if (oldData.results && Array.isArray(oldData.results)) {
          return {
            ...oldData,
            results: oldData.results.map((product: any) =>
              product.id === productId
                ? {
                    ...product,
                    category_id: selectedCategory.id,
                    category_name: newCategoryName,
                  }
                : product
            ),
          };
        }

        return oldData;
      });

      // 2. Update individual product cache if it exists
      queryClient.setQueryData(['product', productId], (oldProduct: any) => {
        if (oldProduct) {
          return {
            ...oldProduct,
            category_id: selectedCategory.id,
            category_name: newCategoryName,
          };
        }
        return oldProduct;
      });

      // 3. API call in background
      await categoryService.updateProductCategory(productId, selectedCategory.id);

      // 4. After successful API call, invalidate to ensure backend sync (but optimistic update already shows change)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(productId) }),
        queryClient.invalidateQueries({ queryKey: ['categories'] }),
      ]);

      toast({
        title: 'Success',
        description: categoryModal.notifications.categoryUpdated,
      });

      // Call the callback to refresh parent component
      if (onCategoryChanged) {
        onCategoryChanged(selectedCategory);
      }

      handleClose();
    } catch {
      toast({
        title: categoryModal.error.title,
        description: categoryModal.notifications.updateFailed,
        variant: 'destructive',
      });

      // On error, revert the optimistic update by invalidating
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PRODUCT(productId) });
    } finally {
      setIsUpdatingProduct(false);
    }
  };

  // Filter categories by search term
  const filterCategories = (cats: Category[], term: string): Category[] => {
    if (!term) return cats;

    return cats.filter(cat => {
      // Include if name matches
      if (cat.name.toLowerCase().includes(term.toLowerCase())) {
        return true;
      }

      // Include if any children match (recursively)
      if (cat.children?.length) {
        const filteredChildren = filterCategories(cat.children, term);
        if (filteredChildren.length) {
          return true;
        }
      }

      return false;
    });
  };

  // Render a category and its children recursively
  const renderCategory = (category: Category, depth = 0) => {
    const isExpanded = expandedIds.includes(category.id);
    const hasChildren = category.children && category.children.length > 0;
    const isSelected = selectedCategory?.id === category.id;
    const isCurrentProductCategory = isProductMode && currentCategory?.id === category.id;

    return (
      <div key={category.id} className="category-item">
        <div
          className={`
            group flex cursor-pointer items-center rounded p-2 transition-colors hover:bg-accent/60 dark:hover:bg-accent/40
            ${isSelected ? 'border border-primary/50 bg-primary/10 font-medium dark:bg-primary/20' : ''}
            ${isCurrentProductCategory ? 'border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : ''}
          `}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => handleSelectCategory(category)}
          role="treeitem"
          aria-expanded={isExpanded}
          aria-selected={isSelected}
        >
          {hasChildren && (
            <ChevronRight
              className={`mr-2 size-4 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              onClick={e => toggleExpand(category.id, e)}
            />
          )}
          {!hasChildren && <div className="w-6" />}
          <FolderIcon className="mr-2 size-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate text-foreground">{category.name}</span>
          {isCurrentProductCategory && (
            <Badge variant="secondary" className="ml-2 text-xs">
              {categoryModal.states.current}
            </Badge>
          )}
          {isSelected && <CheckCircle className="ml-2 size-4 shrink-0 text-primary" />}
        </div>
        {isExpanded && hasChildren && (
          <div className="ml-1" role="group">
            {category.children!.map(child => renderCategory(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  // Filtered categories based on search
  const filteredCategories = searchTerm ? filterCategories(categories, searchTerm) : categories;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[85vh] max-w-5xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>{title || categoryModal.title}</DialogTitle>
          <DialogDescription>{description || categoryModal.description}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-4 lg:grid lg:grid-cols-5">
          {/* Left Panel - Category Tree */}
          <div className="flex min-h-0 flex-col space-y-4 lg:col-span-2">
            <div className="shrink-0 space-y-2">
              <Label htmlFor="search">{categoryModal.search.label}</Label>
              <Input
                id="search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={categoryModal.search.placeholder}
                className="h-9"
              />
            </div>

            {/* Scrollable Category Tree */}
            <div className="flex-1 overflow-hidden rounded-lg border border-border bg-muted/30 p-3 dark:bg-muted/20">
              <div className="category-modal-tree -mr-2 h-full overflow-y-auto pr-2">
                {isLoading ? (
                  <div className="flex h-32 items-center justify-center text-muted-foreground">
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {categoryModal.loading.categories}
                  </div>
                ) : isError ? (
                  <Alert variant="destructive" className="m-2">
                    <AlertTitle>{categoryModal.error.title}</AlertTitle>
                    <AlertDescription className="flex flex-col space-y-2">
                      <span>{categoryModal.error.loadFailed}</span>
                      <Button
                        onClick={() => refetch()}
                        variant="outline"
                        size="sm"
                        className="w-fit"
                      >
                        <RefreshCw className="mr-2 size-4" />
                        {categoryModal.error.retry}
                      </Button>
                    </AlertDescription>
                  </Alert>
                ) : filteredCategories.length > 0 ? (
                  <div className="space-y-1">
                    {filteredCategories.map(category => renderCategory(category))}
                  </div>
                ) : (
                  <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                    {searchTerm
                      ? categoryModal.states.noMatching
                      : categoryModal.states.noCategories}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Actions */}
          <div className="flex min-h-0 flex-col lg:col-span-3">
            <Tabs
              defaultValue={isProductMode ? 'assign' : 'create'}
              className="flex flex-1 flex-col"
            >
              <TabsList
                className={`mb-4 grid w-full shrink-0 ${isProductMode ? 'grid-cols-3' : 'grid-cols-2'}`}
              >
                {isProductMode && (
                  <TabsTrigger value="assign">{categoryModal.tabs.assign}</TabsTrigger>
                )}
                <TabsTrigger value="create">{categoryModal.tabs.create}</TabsTrigger>
                <TabsTrigger value="edit" disabled={!selectedCategory}>
                  {categoryModal.tabs.edit}
                </TabsTrigger>
              </TabsList>

              {isProductMode && (
                <TabsContent value="assign" className="flex-1 overflow-y-auto">
                  <div className="space-y-6 p-4 pr-6">
                    <div>
                      <h3 className="mb-3 text-lg font-semibold">{categoryModal.assign.title}</h3>
                      {selectedCategory ? (
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            {categoryModal.assign.selectedCategory}{' '}
                            <span className="font-medium">{selectedCategory.name}</span>
                          </p>
                          {currentCategory && currentCategory.id === selectedCategory.id ? (
                            <Badge variant="secondary" className="text-sm">
                              {categoryModal.assign.alreadyCurrent}
                            </Badge>
                          ) : (
                            <Button
                              onClick={handleAssignCategory}
                              disabled={isUpdatingProduct}
                              className="w-fit"
                              size="default"
                            >
                              {isUpdatingProduct ? (
                                <>
                                  <Loader2 className="mr-2 size-4 animate-spin" />
                                  {categoryModal.loading.updating}
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="mr-2 size-4" />
                                  {categoryModal.assign.assignButton}
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {categoryModal.assign.pleaseSelect}
                        </p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="create" className="flex-1 overflow-y-auto">
                <div className="space-y-6 p-4 pr-6">
                  <div>
                    <h3 className="mb-3 text-lg font-semibold">{categoryModal.create.title}</h3>
                    {selectedCategory && (
                      <p className="mb-3 text-sm text-muted-foreground">
                        {categoryModal.create.subcategoryUnder}{' '}
                        <span className="font-medium">{selectedCategory.name}</span>
                      </p>
                    )}
                  </div>

                  <PermissionGuard permission="category.add">
                    <div className="space-y-3">
                      <Label htmlFor="new-category">{categoryModal.create.label}</Label>
                      <div className="flex space-x-3">
                        <Input
                          id="new-category"
                          value={newCategoryName}
                          onChange={e => setNewCategoryName(e.target.value)}
                          placeholder={categoryModal.create.label}
                          disabled={isCreating}
                          className="flex-1"
                        />
                        <Button
                          onClick={handleCreateCategory}
                          disabled={!newCategoryName || isCreating}
                          size="default"
                        >
                          {isCreating ? (
                            <>
                              <Loader2 className="mr-2 size-4 animate-spin" />
                              {categoryModal.loading.creating}
                            </>
                          ) : (
                            <>
                              <PlusIcon className="mr-2 size-4" />
                              {categoryModal.create.button}
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {selectedCategory
                          ? categoryModal.create.helperSubcategory.replace(
                              '{{parentName}}',
                              selectedCategory.name
                            )
                          : categoryModal.create.helperTopLevel}
                      </p>
                    </div>
                  </PermissionGuard>
                </div>
              </TabsContent>

              <TabsContent value="edit" className="flex-1 overflow-y-auto">
                {selectedCategory && (
                  <div className="space-y-6 p-4 pr-6">
                    <div>
                      <h3 className="mb-3 text-lg font-semibold">{categoryModal.edit.title}</h3>
                      <p className="mb-3 text-sm text-muted-foreground">
                        {categoryModal.edit.currentlyEditing}{' '}
                        <span className="font-medium">{selectedCategory.name}</span>
                      </p>
                    </div>

                    <PermissionGuard permission="category.change">
                      <div className="space-y-3">
                        <Label htmlFor="edit-category">{categoryModal.edit.label}</Label>
                        <div className="flex space-x-3">
                          <Input
                            id="edit-category"
                            value={editCategoryName}
                            onChange={e => setEditCategoryName(e.target.value)}
                            placeholder={categoryModal.edit.label}
                            disabled={isUpdating}
                            className="flex-1"
                          />
                          <Button
                            onClick={handleUpdateCategory}
                            disabled={
                              !editCategoryName ||
                              isUpdating ||
                              editCategoryName === selectedCategory.name
                            }
                            size="default"
                          >
                            {isUpdating ? (
                              <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                {categoryModal.loading.updating}
                              </>
                            ) : (
                              <>
                                <Save className="mr-2 size-4" />
                                {categoryModal.edit.updateButton}
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </PermissionGuard>

                    <PermissionGuard permission="category.delete">
                      <div className="border-t pt-4">
                        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="default">
                              <Trash className="mr-2 size-4" />
                              {categoryModal.edit.deleteButton}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {categoryModal.deleteDialog.title}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {categoryModal.deleteDialog.description.replace(
                                  '{{categoryName}}',
                                  selectedCategory.name
                                )}
                                {selectedCategory.children &&
                                  selectedCategory.children.length > 0 && (
                                    <span className="mt-2 block font-semibold text-destructive">
                                      {categoryModal.deleteDialog.warning}
                                    </span>
                                  )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {categoryModal.deleteDialog.cancel}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleDeleteCategory}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                disabled={isDeleting}
                              >
                                {isDeleting ? (
                                  <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    {categoryModal.loading.deleting}
                                  </>
                                ) : (
                                  categoryModal.deleteDialog.delete
                                )}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </PermissionGuard>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter className="mt-6 shrink-0">
          <Button variant="outline" onClick={handleClose} size="default">
            {categoryModal.buttons.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
